import { createWidget } from './core';
import { detectLocale, setLocale } from './i18n';
import { normaliseApiUrl } from './lib/origin';
import { LOCATION_CHANGE_EVENT } from './storage';

interface BootConfig {
  token: string;
  apiUrl: string;
  /** No token + no apiUrl → demo mode: submit logs to console, no network call. */
  demoMode: boolean;
  /** URL JWT carried in ?pinmerge_auth=… by the magic-link callback or the
   *  dashboard "open in prototype" button. The widget exchanges it on mount
   *  to bind the cookie's creatorToken to a reviewer profile. */
  initialAuthHandoffJwt: string | null;
  /** Prototype-version slug from ?pinmerge_v=. The widget tags every new
   *  comment with this; the server falls back to the project's default
   *  version when null. Read once at boot and kept stable — we don't react
   *  to runtime URL changes (the param is sticky in the prototype URL the
   *  reviewer was sent to). */
  versionSlug: string | null;
}

const HOST_ID = 'pinmerge-host';
const REVIEW_PARAM = 'pinmerge';
const REVIEW_FLAG_KEY = 'pinmerge:review:active';
const AUTH_HANDOFF_PARAM = 'pinmerge_auth';
const VERSION_PARAM = 'pinmerge_v';

function readBootConfig(): BootConfig | null {
  const tag =
    document.currentScript ??
    document.querySelector<HTMLScriptElement>('script[data-pinmerge], script[data-token][src*="widget"]');

  // dev fallback: window.__PINMERGE_DEV__
  const dev = (globalThis as { __PINMERGE_DEV__?: Partial<BootConfig> }).__PINMERGE_DEV__;

  const token = (tag?.getAttribute('data-token') ?? dev?.token ?? '').trim();
  // Defensive normalisation: handles stray whitespace AND a misconfigured
  // `www.` host prefix (would 308-redirect on the dashboard side and break
  // CORS, since redirects strip Access-Control-Allow-Origin). See origin.ts.
  const apiUrlRaw = tag?.getAttribute('data-api-url') ?? dev?.apiUrl ?? __API_URL__;
  const apiUrl = normaliseApiUrl(apiUrlRaw);
  if (apiUrlRaw.trim() !== apiUrl) {
    console.info(
      `[pinmerge] normalised apiUrl ${JSON.stringify(apiUrlRaw)} → ${JSON.stringify(apiUrl)}`,
    );
  }

  // No token at all → demo mode (local-only, no network). Useful for the playground page.
  const demoMode = !token || !apiUrl;
  if (demoMode) {
    console.info(
      '[pinmerge] demo mode — no token / api configured. Local-only, no network calls.',
    );
  }

  return { token, apiUrl, demoMode, initialAuthHandoffJwt: null, versionSlug: null };
}

// Pinmerge prototype-version slug carried by the URL. Returns null when the
// param is absent or malformed. Unlike the auth handoff JWT we DO NOT strip
// this from the URL — the slug is sticky and the reviewer needs it on every
// navigation inside the prototype (refresh, deep-link sharing, …).
function readVersionSlug(): string | null {
  const raw = new URLSearchParams(window.location.search).get(VERSION_PARAM);
  if (!raw) return null;
  const trimmed = raw.trim().toLowerCase();
  if (trimmed.length === 0 || trimmed.length > 64) return null;
  if (!/^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]?$/.test(trimmed)) return null;
  return trimmed;
}

function isReviewActive(): boolean {
  try {
    return localStorage.getItem(REVIEW_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function setReviewActive(active: boolean): void {
  try {
    if (active) localStorage.setItem(REVIEW_FLAG_KEY, '1');
    else localStorage.removeItem(REVIEW_FLAG_KEY);
  } catch {
    /* private-mode browsers reject localStorage; silently ignore — gate just won't persist */
  }
}

// Reads ?pinmerge=1 / ?pinmerge=0 from the current URL. If present, applies the state
// change (activate or deactivate) and rewrites the URL without the param so the activation
// link doesn't get accidentally shared further on by copy-paste from the address bar.
function consumeUrlActivation(): void {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(REVIEW_PARAM);
  if (value === null) return;

  if (value === '1') setReviewActive(true);
  else if (value === '0') setReviewActive(false);

  params.delete(REVIEW_PARAM);
  rewriteSearch(params);
}

// Reads + strips ?pinmerge_auth=… from the URL. Returns the JWT (or null) so we
// can pass it to App for exchange after mount. Stripping happens unconditionally
// — even if the JWT is invalid or expired, we don't want it lingering in the
// address bar / browser history / accidental copy-paste.
function consumeAuthHandoff(): string | null {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(AUTH_HANDOFF_PARAM);
  if (value === null) return null;
  params.delete(AUTH_HANDOFF_PARAM);
  rewriteSearch(params);
  return value;
}

function rewriteSearch(params: URLSearchParams): void {
  const qs = params.toString();
  const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
  window.history.replaceState({}, '', newUrl);
}

// Script-tag channel mount wrapper. The document-level concerns stay here —
// HOST_ID idempotency and the window.__pinMerge stamping — then the shared
// createWidget() does the actual Shadow-Root mount + render + teardown. The
// in-widget exit flips the localStorage review gate off (channel-specific; the
// package channel's unmount never touches it), then tears down the DOM.
function mount(config: BootConfig): void {
  if (document.getElementById(HOST_ID)) return; // idempotent

  // Stamp the resolved runtime config on window so testers can inspect the
  // actual apiUrl + dashboard origin we're calling from DevTools. Useful when
  // hunting "why is this resource being fetched from the wrong URL" reports.
  (window as unknown as { __pinMerge: Record<string, unknown> }).__pinMerge = {
    version: __WIDGET_VERSION__,
    sha: __BUILD_SHA__,
    buildTime: __BUILD_TIME__,
    apiUrl: config.apiUrl,
    dashboardOrigin: config.apiUrl.replace(/\/api\/?$/, ''),
    demoMode: config.demoMode,
  };

  createWidget(config, { onExit: () => setReviewActive(false) });
}

let historyPatched = false;
function installHistoryPatch(): void {
  if (historyPatched) return;
  historyPatched = true;

  // Monkey-patch pushState + replaceState so SPA navigations dispatch a
  // synthetic event. PostHog does it the same way for the same reason —
  // there's no native pushState event and frameworks vary in what they fire.
  // popstate is native (back/forward) and just needs to be forwarded.
  const wrap = (method: 'pushState' | 'replaceState') => {
    const original = history[method];
    history[method] = function (this: History, ...args: Parameters<History[typeof method]>) {
      const result = original.apply(this, args);
      // Dispatch async via microtask so the URL has already updated by the
      // time listeners read window.location. Without this, a listener that
      // reads location.pathname immediately would still see the old value
      // on some browsers.
      queueMicrotask(() => window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT)));
      return result;
    };
  };
  wrap('pushState');
  wrap('replaceState');
  window.addEventListener('popstate', () => {
    window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
  });
}

function boot(): void {
  // Stamp the build identity on window before any early returns so testers can
  // always answer "which bundle am I running?" from DevTools, even when the
  // widget stays dormant on this page.
  (window as unknown as { __pinMerge: object }).__pinMerge = Object.freeze({
    version: __WIDGET_VERSION__,
    sha: __BUILD_SHA__,
    buildTime: __BUILD_TIME__,
  });

  // History patch runs regardless of review mode so SPA navigations are tracked
  // (and re-bucketed) the moment the widget activates — including a navigation
  // that happened while the widget was still dormant.
  installHistoryPatch();

  const config = readBootConfig();
  if (!config) return;

  // Resolve locale once at boot — URL param > script data-locale > navigator.
  // After login the handoff response can override it (App calls setLocale).
  setLocale(detectLocale());

  consumeUrlActivation();

  // Auth-handoff lands as a URL param from the magic-link callback. If we
  // see one, the user clearly intends to enter review mode — flip the flag
  // even if it isn't already on, so the magic-link round trip ends in an
  // active widget instead of a dormant one.
  const authJwt = consumeAuthHandoff();
  if (authJwt) {
    setReviewActive(true);
    config.initialAuthHandoffJwt = authJwt;
  }

  config.versionSlug = readVersionSlug();

  if (!isReviewActive()) {
    // No URL param + no persisted flag → widget stays dormant. End-users of the prototype
    // see nothing.
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(config), { once: true });
  } else {
    mount(config);
  }
}

boot();
